import { useState, useRef, useEffect, useCallback } from 'react';
import * as S from './styled';

const formatTime = (secs) => {
    if (!secs || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
};

const THRESHOLD = 100;

const PlayIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z" />
    </svg>
);

const PauseIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
    </svg>
);

const VolumeOnIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z" />
    </svg>
);

const VolumeOffIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
    </svg>
);

const FullscreenIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
    </svg>
);

export function Video({
    url,
    initialTime = 0,
    onThresholdReached,
    onTimeUpdate,
    onSaveProgress
}) {
    const videoRef = useRef(null);
    const wrapperRef = useRef(null);

    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(1);
    const [muted, setMuted] = useState(false);
    const [watchedPercent, setWatchedPercent] = useState(0);
    const [thresholdReached, setThresholdReached] = useState(false);
    const [showRipple, setShowRipple] = useState(false);

    const maxReachedRef = useRef(0);
    const thresholdReachedRef = useRef(false);
    const lastSavedTimeRef = useRef(Number(initialTime) || 0);
    const initialTimeAppliedRef = useRef(false);

    const togglePlay = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        if (v.paused) {
            v.play();
        } else {
            v.pause();
        }
        setShowRipple(true);
        setTimeout(() => setShowRipple(false), 500);
    }, []);

    const toggleMute = useCallback((e) => {
        e.stopPropagation();
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setMuted(v.muted);
    }, []);

    const handleVolume = useCallback((e) => {
        e.stopPropagation();
        const v = videoRef.current;
        if (!v) return;
        const val = Number(e.target.value) / 100;
        v.volume = val;
        setVolume(val);
        if (val > 0 && v.muted) {
            v.muted = false;
            setMuted(false);
        }
    }, []);

    const handleFullscreen = useCallback((e) => {
        e.stopPropagation();
        const el = wrapperRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            el.requestFullscreen?.();
        } else {
            document.exitFullscreen?.();
        }
    }, []);

    const handleTimeUpdate = useCallback(() => {
        const v = videoRef.current;
        if (!v || !v.duration || isNaN(v.duration)) return;

        const curTime = v.currentTime;
        const curSec = Math.floor(curTime);
        const percent = Math.floor((curTime / v.duration) * 100);
        if (percent > maxReachedRef.current) {
            maxReachedRef.current = percent;
        }
        setCurrentTime(curTime);
        setWatchedPercent(maxReachedRef.current);

        if (onTimeUpdate) {
            onTimeUpdate(curSec);
        }

        if (Math.abs(curSec - lastSavedTimeRef.current) >= 30) {
            lastSavedTimeRef.current = curSec;
            if (onSaveProgress) {
                onSaveProgress(curSec);
            }
        }

        if (maxReachedRef.current >= THRESHOLD) {
            if (!thresholdReachedRef.current) {
                thresholdReachedRef.current = true;
                setThresholdReached(true);
                if (onThresholdReached) onThresholdReached();
            }
        }
    }, [onThresholdReached, onTimeUpdate, onSaveProgress]);

    const handlePause = useCallback(() => {
        setIsPlaying(false);
        const v = videoRef.current;
        if (!v) return;
        const curSec = Math.floor(v.currentTime);
        if (Math.abs(curSec - lastSavedTimeRef.current) >= 5) {
            lastSavedTimeRef.current = curSec;
            if (onSaveProgress) {
                onSaveProgress(curSec);
            }
        }
    }, [onSaveProgress]);

    const handleEnded = useCallback(() => {
        setIsPlaying(false);
        const v = videoRef.current;
        if (!v || !v.duration) return;
        const finalSec = Math.floor(v.duration);
        if (Math.abs(finalSec - lastSavedTimeRef.current) >= 1) {
            lastSavedTimeRef.current = finalSec;
            if (onSaveProgress) {
                onSaveProgress(finalSec);
            }
        }
        if (onTimeUpdate) {
            onTimeUpdate(finalSec);
        }
    }, [onSaveProgress, onTimeUpdate]);

    const handleSeeking = useCallback(() => {
        const v = videoRef.current;
        if (!v || !v.duration) return;
        const maxAllowed = (maxReachedRef.current / 100) * v.duration;
        if (v.currentTime > maxAllowed + 1) {
            v.currentTime = maxAllowed;
        }
    }, []);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;

        const applyInitialTime = () => {
            if (initialTimeAppliedRef.current) return;
            const d = v.duration;
            if (!d || isNaN(d) || d <= 0) return;

            initialTimeAppliedRef.current = true;
            setDuration(d);

            const startTime = Number(initialTime) || 0;
            const startSec = Math.min(startTime, d);

            if (startTime > 0) {
                v.currentTime = startSec;
                setCurrentTime(startSec);
            }

            const percent = Math.floor((startSec / d) * 100);
            if (percent > maxReachedRef.current) {
                maxReachedRef.current = percent;
            }
            setWatchedPercent(maxReachedRef.current);

            if (maxReachedRef.current >= THRESHOLD) {
                thresholdReachedRef.current = true;
                setThresholdReached(true);
                if (onThresholdReached) {
                    onThresholdReached();
                }
            }
        };

        if (v.readyState >= 1) {
            applyInitialTime();
        }

        v.addEventListener('loadedmetadata', applyInitialTime);
        v.addEventListener('canplay', applyInitialTime);
        v.addEventListener('canplaythrough', applyInitialTime);
        return () => {
            v.removeEventListener('loadedmetadata', applyInitialTime);
            v.removeEventListener('canplay', applyInitialTime);
            v.removeEventListener('canplaythrough', applyInitialTime);
        };
    }, [initialTime, onThresholdReached]);

    useEffect(() => {
        if (thresholdReached && onThresholdReached) {
            onThresholdReached();
        }
    }, [thresholdReached, onThresholdReached]);

    const volPct = muted ? 0 : Math.round(volume * 100);

    return (
        <S.VideoWrapper ref={wrapperRef}>
            <S.VideoElement
                ref={videoRef}
                src={url}
                autoPlay={false}
                preload="metadata"
                playsInline
                onTimeUpdate={handleTimeUpdate}
                onSeeking={handleSeeking}
                onPlay={() => setIsPlaying(true)}
                onPause={handlePause}
                onEnded={handleEnded}
                onContextMenu={(e) => e.preventDefault()}
            />

            <S.BlockOverlay onClick={togglePlay} onContextMenu={(e) => e.preventDefault()} />

            {showRipple && <S.RippleCircle />}

            <S.ProgressTrack>
                <S.ProgressFill $percent={watchedPercent} $reached={thresholdReached} />
            </S.ProgressTrack>

            <S.ControlBar>
                <S.IconButton onClick={(e) => { e.stopPropagation(); togglePlay(); }} title={isPlaying ? 'Pausar' : 'Reproduzir'}>
                    {isPlaying ? <PauseIcon /> : <PlayIcon />}
                </S.IconButton>

                <S.VolumeWrapper>
                    <S.IconButton onClick={toggleMute} title={muted ? 'Ativar som' : 'Silenciar'}>
                        {muted || volPct === 0 ? <VolumeOffIcon /> : <VolumeOnIcon />}
                    </S.IconButton>
                    <S.VolumeSlider
                        type="range"
                        min="0"
                        max="100"
                        value={volPct}
                        $vol={volPct}
                        onChange={handleVolume}
                        onClick={(e) => e.stopPropagation()}
                        title="Volume"
                    />
                </S.VolumeWrapper>

                <S.TimeLabel>{formatTime(currentTime)} / {formatTime(duration)}</S.TimeLabel>

                <S.Spacer />

                <S.IconButton onClick={handleFullscreen} title="Tela cheia">
                    <FullscreenIcon />
                </S.IconButton>
            </S.ControlBar>

            <S.ProgressOverlay>
                <S.ProgressBarTrack>
                    <S.ProgressBarFill $percent={watchedPercent} $reached={thresholdReached} />
                </S.ProgressBarTrack>
                <S.ProgressLabel $reached={thresholdReached}>
                    {!thresholdReached && <S.Dot $reached={false} />}
                    {`${watchedPercent}% assistido`}
                </S.ProgressLabel>
            </S.ProgressOverlay>
        </S.VideoWrapper>
    );
}